import {
	ADD_WHITELIST_ITEM,
	API_FAILURE,
	AddWhiteListItemAction,
	ApiFailureAction,
	CREATE_ORG,
	FEATURE_LIST_SUCCESS,
	FETCH_FEATURE_LIST,
	FETCH_LICENCE_LIST,
	FETCH_ORG_CSV_DATA,
	FETCH_ORG_DETAIL,
	FETCH_ORG_LIST,
	FETCH_ORG_WHITELIST,
	FeatureListFetchAction,
	FeatureListSuccessAction,
	FetchOrgWhiteListAction,
	INVITE_ADMIN,
	InviteAdminAction,
	LICENCE_LIST_SUCCESS,
	LicenceListFetchAction,
	LicenceListSuccessAction,
	ORG_CSV_DATA_SUCCESS,
	ORG_DETAIL_SUCCESS,
	ORG_LIST_SUCCESS,
	OrgCSVDataFetchAction,
	OrgCSVDataSuccessAction,
	OrgCreateAction,
	OrgDetailFetchAction,
	OrgDetailSuccessAction,
	OrgListFetchAction,
	OrgListSuccessAction,
	OrgUpdateAction,
	REMOVE_WHITELIST_ITEM,
	RESET_API_STATE,
	RemoveWhiteListItemAction,
	ResetApiStateAction,
	UPDATE_ORG
} from './action-types';

import Feature from '../../types/feature';
import Licence from '../../types/licence';
import Organization from '../../types/organization';

/**
 * Creates an action object to initiate fetching the organization list.
 * 
 * @returns {OrgListFetchAction} - The action object for fetching the organization list.
 */
export const fetchOrgList = (): OrgListFetchAction => ({
	type: FETCH_ORG_LIST
});

/**
 * Creates an action object to indicate a successful organization list fetch.
 * 
 * @param {Array<OrganizationDetail>} orgList - The fetched list of organizations.
 * @returns {OrgListSuccessAction} - The action object for successful organization list fetch.
 */
export const orgListSuccess = (orgList: Array<Organization>): OrgListSuccessAction => ({
	type: ORG_LIST_SUCCESS,
	payload: orgList
});

/**
 * Action creator for the `ORG_DETAIL_SUCCESS` action.
 * This action is dispatched when the details of a specific organization are successfully fetched.
 *
 * @param org The `Organization` object containing the detailed information.
 * @returns An action object of type `OrgDetailSuccessAction` with the organization data as its payload.
 */
export const orgDetailSuccess = (org: Organization): OrgDetailSuccessAction => ({
	type: ORG_DETAIL_SUCCESS,
	payload: org
});

/**
 * Creates an action object to initiate fetching the licence list.
 * 
 * @returns {LicenceListFetchAction} - The action object for fetching the licence list.
 */
export const initLicenceListFetch = (): LicenceListFetchAction => ({
	type: FETCH_LICENCE_LIST
});

/**
 * Creates an action object to indicate a successful licence list fetch.
 * 
 * @param {Array<Licence>} licenceList - The fetched list of licences.
 * @returns {LicenceListSuccessAction} - The action object for successful licence list fetch.
 */
export const licenceListSuccess = (licenceList: Array<Licence>): LicenceListSuccessAction => ({
	type: LICENCE_LIST_SUCCESS,
	payload: licenceList
});

/**
 * Creates an action object to initiate fetching the feature list.
 * 
 * @returns {FeatureListFetchAction} - The action object for fetching the feature list.
 */
export const initFeatureListFetch = (): FeatureListFetchAction => ({
	type: FETCH_FEATURE_LIST
});

/**
 * Creates an action object to indicate a successful feature list fetch.
 * 
 * @param {Array<Feature>} featureList - The fetched list of features.
 * @returns {FeatureListSuccessAction} - The action object for successful feature list fetch.
 */
export const featureListSuccess = (featureList: Array<Feature>): FeatureListSuccessAction => ({
	type: FEATURE_LIST_SUCCESS,
	payload: featureList
});

/**
 * Creates an action object to initiate fetching the details of a specific organization.
 * 
 * @returns {OrgDetailFetchAction} - The action object for fetching organization details.
 */
export const initOrgDetailFetch = (): OrgDetailFetchAction => ({
	type: FETCH_ORG_DETAIL
});

/**
 * Creates an action object to initiate the creation of a new organization.
 * 
 * @returns {OrgCreateAction} - The action object for creating a new organization.
 */
export const initOrgCreate = (): OrgCreateAction => ({
	type: CREATE_ORG,
});

/**
 * Creates an action object to initiate updating an existing organization's details.
 * 
 * @returns {OrgUpdateAction} - The action object for updating an organization.
 */
export const initOrgUpdate = (): OrgUpdateAction => ({
	type: UPDATE_ORG,
});

/**
 * Creates an action object to initiate the process of inviting an admin to the organization.
 *
 * @returns {InviteAdminAction} - The action object for inviting an administrator.
 */
export const initAdminInvite = (): InviteAdminAction => ({
	type: INVITE_ADMIN,
});

/**
 * Creates an action to initiate fetching the whitelist.
 *
 * @returns {FetchOrgWhiteListAction} - The fetch whitelist action.
 */
export const initWhiteListFetch = (): FetchOrgWhiteListAction => ({
	type: FETCH_ORG_WHITELIST,
});

/**
 * Creates an action to initiate adding a whitelist item.
 *
 * @returns {AddWhiteListItemAction} - The add whitelist item action.
 */
export const initWhiteListItemAdd = (): AddWhiteListItemAction => ({
	type: ADD_WHITELIST_ITEM,
});

/**
 * Creates an action to initiate removing a whitelist item.
 *
 * @returns {RemoveWhiteListItemAction} - The remove whitelist item action.
 */
export const initWhiteListItemRemove = (): RemoveWhiteListItemAction => ({
	type: REMOVE_WHITELIST_ITEM,
});

/**
 * Action creator for initializing the fetching of organization CSV data.
 * @returns {OrgCSVDataFetchAction} An action object with the type `FETCH_ORG_CSV_DATA`.
 */
export const initOrgCSVDataFetch = (): OrgCSVDataFetchAction => ({
	type: FETCH_ORG_CSV_DATA
});

/**
 * Action creator for a successful fetch of organization CSV data.
 * @param {Array<Array<string>>} rows - A two-dimensional array representing the fetched CSV data rows.
 * @returns {OrgCSVDataSuccessAction} An action object with the success type and the fetched data as the payload.
 */
export const orgCSVDataFetchSuccess = (rows: Array<Array<string>>): OrgCSVDataSuccessAction => ({
	type: ORG_CSV_DATA_SUCCESS,
	payload: rows
});

/**
 * Creates an action object to indicate an API call failure with an error message.
 *
 * This function takes an error message as input and returns an action object
 * with the type `API_FAILURE` and the error message in the payload.
 *
 * @param {string} error - The error message encountered during the API call.
 * @returns {ApiFailureAction} - The action object for API call failure.
 */
export const apiFailure = (error: string): ApiFailureAction => ({
	type: API_FAILURE,
	payload: error
});

/**
 * Action creator for resetting the API state.
 * This action is typically used to clear any previous API errors or loading states.
 * @returns {ResetApiStateAction} An action object with the type `RESET_API_STATE`.
 */
export const resetApiState = (): ResetApiStateAction => ({
	type: RESET_API_STATE
});